/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.30.0
 */

import { Fragment, useCallback } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@/main/components/common/Title/Title';
import withAsyncStateManagement from '@/main/modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import { makeSelectTranslationWizardCollationReport } from '@/main/modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@/main/modules/internationalization/types/common/pages';
import Wizard, { IWizardProps } from '@/main/modules/wizard/components/Wizard';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@/main/modules/wizard/wizardConstants';
import { IWizardState } from '@/main/modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@/main/modules/wizard/wizardSelectors';
import { IRouteParams } from '@/main/routes/constants/routesConstants';
import { IWizardCollationReportTranslation } from '@modules/internationalization/types/wizards/collationReport';
import { IRootReducer } from '@reducers/rootReducer';

import { wizardCollationReportHandleNextAction } from '../wizardCollationReportActions';
import { EWizardCollationReportSteps } from '../wizardCollationReportConstant';
import wizardCollationReportReducer, { IWizardCollationReportReducer } from '../wizardCollationReportReducer';
import { makeSelectWizardCollationReportIsStepValid } from '../wizardCollationReportSelectors';
import WizardCollationReportStepChoose from './WizardCollationReportStepChoose';
import WizardCollationReportStepConfirm from './WizardCollationReportStepConfirm';

const WizardCollationReport = ({
    basketType,
    isStepValid,
    stepName,
    translationCollationReport,
    handleNext,
}: IConnectedProps & IDispatchProps & Pick<IWizardProps, 'basketType'>) => {
    const { clientId } = useParams<IRouteParams>();

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardCollationReportSteps.ChooseStep:
                return <WizardCollationReportStepChoose />;
            case EWizardCollationReportSteps.ConfirmStep:
                return <WizardCollationReportStepConfirm />;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale
                lazy
                fileNames={[TranslationFileNamesEnum.wizardCollationReport, TranslationFileNamesEnum.wizard]}
            />

            <Title pageName={ECommonTranslationPages.wizardCollationReport} />

            <Wizard
                basketType={basketType}
                entityId={clientId ? parseInt(clientId) : WIZARD_ALL_ENTITIES}
                isNextDisabled={!isStepValid}
                name={EWizardName.COLLATION_REPORT}
                translationProps={{
                    error: translationCollationReport.error,
                    steps: translationCollationReport.steps,
                    title: translationCollationReport.title,
                }}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>
        </Fragment>
    );
};

export interface IConnectedProps
    extends Pick<IWizardState, 'stepName'>,
        Pick<IWizardCollationReportReducer, 'isStepValid'> {
    translationCollationReport: IWizardCollationReportTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectWizardCollationReportIsStepValid(),
    stepName: makeSelectWizardStepName(),
    translationCollationReport: makeSelectTranslationWizardCollationReport(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardCollationReportHandleNextAction()),
});

export default withAsyncStateManagement(connect(mapStateToProps, mapDispatchToProps)(WizardCollationReport), {
    reducer: { wizardCollationReport: wizardCollationReportReducer },
});
